import { useEffect, useState } from 'react';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';
import { Checkbox } from '@/components/atoms/checkbox';
import { StyledInput } from '@/components/atoms/inputs/styled';
import { LoadingButton } from '@/components/atoms/Button';
import { AuthTemplate } from '@/components/template/auth';
import { AppRouters } from '@/utils/constants/routers';
import { FormContainer, SubField } from './styles';
import { useNavigate } from 'react-router-dom';
import { Alert } from '@/components/molecules/alert';
import { useTheme } from '@mui/material/styles';
import { PublicRoute } from '@/components/organisms/route/public';
import { Text } from '@/components/atoms/text';
import { EmailOutlined, PasswordOutlined, PersonOutlined } from '@mui/icons-material';
import { useAlert } from '@/hooks/useAlert';
import { useApolloClient } from '@apollo/client';
import { RegisterValues, UserRole } from '@/graphql/types/user';
import { MUTATION_CREATE_USER } from '@/graphql/mutation/user';
import { useTranslation } from '@/hooks/useTranslation';
import { SafeMarkdown } from '@/components/atoms/markdown/safe';
import { Skeleton } from '@mui/material';
import { useLanguage } from '@/hooks/useLanguage';
import { useAuth } from '@/hooks/useAuth';
import { REMEMBER_ME_KEY } from '@/context/auth';

export default function RegisterPage() {
  const apolloClient = useApolloClient();
  const navigate = useNavigate();
  const theme = useTheme();

  const { t } = useTranslation();
  const { loading: languageLoading } = useLanguage();

  const [loading, setLoading] = useState<boolean>(false);

  const { alert, showAlert, handleCloseAlert } = useAlert();
  const { getUser, getToken } = useAuth();

  const initialValues = {
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    terms: false,
    rememberMe: false,
  };

  const schema = Yup.object().shape({
    name: Yup.string().required(t('messages.error.error.name_required')),
    email: Yup.string().required(t('messages.error.error.email_required')).email(),
    password: Yup.string()
      .required(t('messages.error.error.password_required'))
      .min(6, t('messages.error.error.password_min_characters'))
      .max(15, t('messages.error.error.password_max_characters')),
    confirmPassword: Yup.string()
      .oneOf([Yup.ref('password'), null], t('messages.error.error.passwords_not_compatible'))
      .required(t('messages.error.error.confirm_password_required')),
    terms: Yup.boolean().oneOf([true], t('messages.error.error.terms_required')),
  });

  const registerUser = async (values: RegisterValues) => {
    const { data } = await apolloClient.mutate<{
      createUser: {
        user: {
          photoURL: string;
          phoneNumber: string;
          emailVerified: boolean;
          email: string;
          displayName: string;
          uid: string;
          role: string;
        };
        access_token: string;
      };
    }>({
      mutation: MUTATION_CREATE_USER,
      variables: {
        input: {
          display_name: values.name,
          email: values.email,
          password: values.password,
          disabled: false,
        },
      },
    });

    if (data) {
      getUser({
        ...data.createUser.user,
        role: data.createUser.user.role as UserRole,
      });
      getToken(data.createUser.access_token);
    }
  };

  const handleSubmit = async (values: typeof initialValues) => {
    try {
      setLoading(true);
      localStorage.setItem(REMEMBER_ME_KEY, String(values.rememberMe));
      await registerUser({
        name: values.name,
        email: values.email,
        password: values.password,
        disabled: true,
      });
      navigate(AppRouters.home);
    } catch (error) {
      showAlert('error', t('messages.error.error.registration_failed'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    document.title = 'KYT-e | GBF - Register';
  }, []);

  return (
    <PublicRoute>
      <AuthTemplate
        title={t('page.register.register.title')}
        subtitle={t('page.register.register.description')}
        anchorText={t('label.label.already_have_account')}
        languageLoading={languageLoading}
      >
        <Formik initialValues={initialValues} onSubmit={handleSubmit} validationSchema={schema}>
          {({ errors, values, handleChange }) => (
            <Form>
              <FormContainer>
                <StyledInput
                  name="name"
                  label={
                    languageLoading ? (
                      <Skeleton
                        variant="text"
                        width={100}
                        height={20}
                        sx={{ transform: 'none' }}
                        animation="wave"
                      />
                    ) : (
                      <>{t('label.label.input_name')}</>
                    )
                  }
                  margin="none"
                  type="text"
                  startIcon={<PersonOutlined color="action" />}
                  onChange={handleChange}
                  helperText={errors.name}
                  error={Boolean(errors.name)}
                />

                <StyledInput
                  name="email"
                  label={
                    languageLoading ? (
                      <Skeleton
                        variant="text"
                        width={100}
                        height={20}
                        sx={{ transform: 'none' }}
                        animation="wave"
                      />
                    ) : (
                      <>{t('label.label.email')}</>
                    )
                  }
                  margin="none"
                  type="email"
                  startIcon={<EmailOutlined color="action" />}
                  onChange={handleChange}
                  helperText={errors.email}
                  error={Boolean(errors.email)}
                />

                <StyledInput
                  name="password"
                  label={
                    languageLoading ? (
                      <Skeleton
                        variant="text"
                        width={100}
                        height={20}
                        sx={{ transform: 'none' }}
                        animation="wave"
                      />
                    ) : (
                      <>{t('label.label.input_password')}</>
                    )
                  }
                  margin="none"
                  type="password"
                  startIcon={<PasswordOutlined color="action" />}
                  onChange={handleChange}
                  helperText={errors.password}
                  error={Boolean(errors.password)}
                />

                <StyledInput
                  name="confirmPassword"
                  label={
                    languageLoading ? (
                      <Skeleton
                        variant="text"
                        width={100}
                        height={20}
                        sx={{ transform: 'none' }}
                        animation="wave"
                      />
                    ) : (
                      <>{t('label.label.input_confirm_password')}</>
                    )
                  }
                  margin="none"
                  type="password"
                  startIcon={<PasswordOutlined color="action" />}
                  onChange={handleChange}
                  helperText={errors.confirmPassword}
                  error={Boolean(errors.confirmPassword)}
                />

                <SubField>
                  <Checkbox
                    name="terms"
                    label={
                      languageLoading ? (
                        <Skeleton
                          variant="text"
                          width={200}
                          height={20}
                          sx={{ transform: 'none' }}
                          animation="wave"
                        />
                      ) : (
                        <SafeMarkdown text={t('page.register.register.accept_terms')} />
                      )
                    }
                    onChange={handleChange}
                    checked={values.terms}
                    sx={{
                      display: 'flex',
                      textAlign: 'left',
                    }}
                  />

                  {errors.terms && (
                    <Text variant="caption" color="error" sx={{ margin: theme.spacing(0, 1.75) }}>
                      {errors.terms}
                    </Text>
                  )}
                </SubField>

                <LoadingButton
                  data-testid="register-button"
                  loading={loading}
                  variant="contained"
                  size="large"
                  type="submit"
                  name="register"
                  sx={(theme) => ({
                    color: theme.palette.text.primary,
                    height: '44px',
                  })}
                >
                  {languageLoading ? (
                    <Skeleton
                      variant="text"
                      width={150}
                      height={20}
                      sx={{ transform: 'none' }}
                      animation="wave"
                    />
                  ) : (
                    <>{t('button.button.register')}</>
                  )}
                </LoadingButton>
              </FormContainer>
            </Form>
          )}
        </Formik>
      </AuthTemplate>

      <Alert
        open={alert.open}
        severity={alert.severity}
        message={alert.message}
        autoHideDuration={5000}
        onClose={handleCloseAlert}
      />
    </PublicRoute>
  );
}
